import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Bars3Icon, XMarkIcon, Squares2X2Icon, UserGroupIcon, InboxIcon,
  ChatBubbleLeftRightIcon, Cog6ToothIcon, ArrowRightOnRectangleIcon,
  ChevronDownIcon, ChevronUpIcon, UserCircleIcon, LockClosedIcon, ExclamationTriangleIcon, ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { Logo, useBranding } from '../contexts/BrandingContext';
import HeaderBell from './HeaderBell';
import UpdateHinweis from './UpdateHinweis';
import IssueModal from './IssueModal';
import { meinKontoUrl } from '../util/meinKonto';
import { APP_VERSION } from '../config/version';
import { reviewQueue } from '../api/unimedica';

/**
 * App-Shell — Fork von powerCONTACT/Layout.tsx (2026-09-04).
 *
 * Uebernommen: weisse Sidebar (w-80), dunkler Header, Benutzermenue oben rechts,
 * Mobile-Drawer, Main als Scroll-Container, „Nach oben"-Knopf.
 * Entfernt: Kalender-Widget, Telefonie (SIP, Anrufbanner, Telefon-Symbole),
 * Ticketsuche und Ticket-Symbole. „Problem melden" seit 04.09. wieder drin (IssueModal → Gitea).
 * PowerCare/Unimedica: eigene Navigation, Eingang-Zaehler, Sidebar-Fuss mit Uhr und
 * „Daten bleiben lokal", Footer mit Adresse.
 *
 * Einzige bewusste Abweichung von der Vorlage: das Logo-Band in der Sidebar ist
 * WEISS statt dunkel — ein Praxis-Logo ist auf dunklem Grund oft
 * nicht lesbar.
 */

const navigation = [
  { name: 'Übersicht',  href: '/',          icon: Squares2X2Icon },
  { name: 'Patienten',  href: '/patienten', icon: UserGroupIcon },
  { name: 'Eingang',    href: '/eingang',   icon: InboxIcon },
  { name: 'Fragen',     href: '/chat',      icon: ChatBubbleLeftRightIcon },
  { name: 'Arbeitstisch', href: '/arbeitstisch', icon: ClipboardDocumentListIcon },
];
const navAdmin = { name: 'Verwaltung', href: '/admin', icon: Cog6ToothIcon };

function isActive(href: string, pathname: string): boolean {
  if (href === '/') return pathname === '/';
  return pathname.startsWith(href);
}

function initialen(name: string): string {
  const parts = name.trim().split(/\s+/);
  return (parts.length >= 2 ? parts[0][0] + parts[parts.length - 1][0] : name.slice(0, 2)).toUpperCase();
}

// ── Benutzermenue (Header rechts) ────────────────────────────────────────
function UserMenu() {
  const { user, logout, canSeeAdminArea } = useAuth();
  const { branding } = useBranding();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [showIssue, setShowIssue] = useState(false);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  if (!user) return null;
  const name = user.name || user.username || 'Konto';

  return (
    <div className="relative" ref={ref}>
      {showIssue && <IssueModal onClose={() => setShowIssue(false)} />}
      <button onClick={() => setOpen((o) => !o)} title={user.username ?? name}
        className="flex items-center gap-2 rounded-lg bg-ps-dark-800 px-2 py-1.5 transition-colors hover:bg-ps-dark-700">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-500 text-[11px] font-semibold tracking-wide text-white">
          {initialen(name)}
        </span>
        <span className="hidden max-w-[12rem] truncate text-sm font-medium text-white lg:inline">{name}</span>
        <ChevronDownIcon className="hidden h-4 w-4 text-ps-dark-300 lg:inline" />
      </button>

      {open && (
        <div className="absolute right-0 z-[60] mt-1 w-56 max-w-[calc(100vw-1rem)] overflow-hidden rounded-lg border border-ps-dark-200 bg-white shadow-xl">
          <div className="border-b border-ps-dark-100 px-3 py-2.5">
            <div className="truncate text-sm font-semibold text-ps-dark-900">{name}</div>
            <div className="truncate text-xs text-ps-dark-500">{user.username} · {user.role}</div>
          </div>
          <Link to="/profil" onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 text-sm text-ps-dark-700 hover:bg-ps-dark-50">
            <UserCircleIcon className="h-4 w-4" /> Profil
          </Link>
          {canSeeAdminArea && (
            <Link to="/admin" onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-ps-dark-700 hover:bg-ps-dark-50">
              <Cog6ToothIcon className="h-4 w-4" /> Verwaltung
            </Link>
          )}
          <a href={meinKontoUrl()} onClick={() => setOpen(false)} title="Passwort und Anmeldearten in PowerID verwalten"
            className="flex items-center gap-2 px-3 py-2 text-sm text-ps-dark-700 hover:bg-ps-dark-50">
            <UserCircleIcon className="h-4 w-4" /> Mein Konto
          </a>
          <button onClick={() => { setOpen(false); setShowIssue(true); }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ps-dark-700 hover:bg-ps-dark-50">
            <ExclamationTriangleIcon className="h-4 w-4" /> Problem melden
          </button>
          <button onClick={() => { setOpen(false); void logout(); }}
            className="flex w-full items-center gap-2 border-t border-ps-dark-100 px-3 py-2 text-left text-sm text-rose-700 hover:bg-rose-50">
            <ArrowRightOnRectangleIcon className="h-4 w-4" /> Abmelden
          </button>
          <div className="flex w-full items-center justify-center border-t border-ps-dark-100 px-3 py-1.5 text-[11px] text-ps-dark-400">
            {branding.kurzname} · PowerCare v{APP_VERSION} · lokal
          </div>
        </div>
      )}
    </div>
  );
}

// ── Eingang-Schnellzugriff im Header (Muster HeaderTicketIcons) ───────────
function HeaderInbox({ count }: { count: number }) {
  return (
    <Link to="/eingang" title="Eingang — Dokumente zur Prüfung"
      className="flex min-h-11 min-w-11 flex-col items-center justify-center rounded-md px-2 py-1 transition hover:bg-ps-dark-700">
      <InboxIcon className={`h-5 w-5 ${count > 0 ? 'text-primary-300' : 'text-ps-dark-400'}`} />
      <span className={`text-[11px] font-semibold leading-none numerical ${count > 0 ? 'text-white' : 'text-ps-dark-400'}`}>{count}</span>
    </Link>
  );
}

// ── „Nach oben" — scrollt den <main>-Container, nicht window ─────────────
function ScrollToTopButton({ targetRef }: { targetRef: React.RefObject<HTMLElement> }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const el = targetRef.current;
    if (!el) return;
    const onScroll = () => setShow(el.scrollTop > 300);
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, [targetRef]);
  if (!show) return null;
  return (
    <button onClick={() => targetRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
      className="fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-ps-dark-800 text-white shadow-lg transition hover:bg-ps-dark-700"
      title="Nach oben" aria-label="Nach oben scrollen">
      <ChevronUpIcon className="h-6 w-6" />
    </button>
  );
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const { branding } = useBranding();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [now, setNow] = useState(new Date());
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const { canSeeAdminArea , canSeeAkten } = useAuth();

  // Eingang-Zaehler alle 30 s, Uhr jede Minute (wie im Svelte-Layout).
  useEffect(() => {
    const tick = () => reviewQueue().then((q) => setPendingCount(q.count)).catch(() => {});
    tick();
    const t1 = setInterval(tick, 30_000);
    const t2 = setInterval(() => setNow(new Date()), 60_000);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, []);

  const basis = canSeeAkten ? [...navigation] : navigation.filter((n) => n.href === '/chat');
  const nav = canSeeAdminArea ? [...basis, navAdmin] : basis;
  const dateLabel = now.toLocaleDateString('de-CH', { weekday: 'short', day: '2-digit', month: 'short' });
  const timeLabel = now.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });

  // Sidebar als RENDER-FUNKTION, nicht als Inline-Komponente (sonst remountet
  // React den Baum bei jedem Layout-Render) — Erkenntnis aus powerCONTACT.
  const renderSidebar = (isMobile = false) => (
    <>
      {/* Logo-Band: weiss, das Logo braucht hellen Grund */}
      <Link to="/" onClick={() => isMobile && setSidebarOpen(false)}
        className="flex h-16 flex-shrink-0 items-center justify-center border-b border-gray-200 bg-white px-4 transition-colors hover:bg-gray-50"
        aria-label="Startseite">
        <Logo className="h-12" textClass="text-2xl" />
      </Link>

      <div className="flex flex-1 flex-col overflow-y-auto pb-4 pt-5">
        <nav className="flex-shrink-0 space-y-1 px-2">
          {nav.map((item) => {
            const active = isActive(item.href, location.pathname);
            return (
              <Link key={item.name} to={item.href} onClick={() => isMobile && setSidebarOpen(false)}
                className={`${active
                  ? 'border-l-4 border-primary-500 bg-primary-100 text-primary-900'
                  : 'border-l-4 border-transparent text-ps-dark-700 hover:bg-ps-dark-100 hover:text-ps-dark-900'
                } group flex items-center rounded-r-md px-3 py-2 text-sm font-medium`}>
                <item.icon className={`${active ? 'text-primary-500' : 'text-ps-dark-400 group-hover:text-ps-dark-600'} mr-3 h-6 w-6`} />
                <span className="flex-1 truncate">{item.name}</span>
                {item.href === '/eingang' && pendingCount > 0 && (
                  <span className="numerical inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary-500 px-1.5 text-[10.5px] font-semibold text-white">
                    {pendingCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Sidebar-Fuss: Status + Uhr + Datenschutz-Hinweis */}
      <div className="flex-shrink-0 space-y-1.5 border-t border-gray-200 px-4 py-3">
        <div className="flex items-center gap-2 text-[10.5px]">
          <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-emerald-500" />
          <span className="font-medium text-emerald-700">System bereit</span>
          <span className="numerical ml-auto text-ps-dark-400">{dateLabel} · {timeLabel}</span>
        </div>
        <div className="flex items-center gap-1 text-[10px] text-ps-dark-400">
          <LockClosedIcon className="h-3 w-3" /> Daten bleiben lokal — keine Cloud
        </div>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile-Drawer — nur bei geoeffnetem Zustand mounten */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex h-full w-full max-w-xs flex-col bg-white">
            <div className="absolute right-0 top-0 -mr-14 p-1">
              <button type="button" onClick={() => setSidebarOpen(false)} aria-label="Menü schliessen"
                className="flex h-12 w-12 items-center justify-center rounded-full focus:bg-gray-600 focus:outline-none">
                <XMarkIcon className="h-6 w-6 text-white" />
              </button>
            </div>
            {renderSidebar(true)}
          </div>
        </div>
      )}

      {/* Desktop-Sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex h-screen w-80 flex-col overflow-hidden border-r border-gray-200 bg-white">
          {renderSidebar(false)}
        </div>
      </div>

      {/* Rechte Spalte */}
      <div className="flex w-0 flex-1 flex-col overflow-hidden">
        {/* Desktop-Header (dunkel, wie powerCONTACT) */}
        <div className="relative z-10 hidden h-16 flex-shrink-0 items-center justify-end gap-3 bg-ps-dark-900 px-6 shadow lg:flex">
          <HeaderInbox count={pendingCount} />
          <HeaderBell />
          <UserMenu />
        </div>

        {/* Mobile-Header */}
        <div className="relative z-10 flex h-16 flex-shrink-0 items-center bg-ps-dark-900 shadow lg:hidden">
          <button type="button" onClick={() => setSidebarOpen(true)} aria-label="Menü öffnen"
            className="relative flex min-h-11 min-w-11 shrink-0 items-center justify-center px-3 text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 sm:px-4">
            <Bars3Icon className="h-6 w-6" />
            {pendingCount > 0 && (
              <span className="numerical absolute -right-0.5 top-2 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary-500 px-1 text-[9px] font-semibold text-white">
                {pendingCount}
              </span>
            )}
          </button>
          <div className="flex min-w-0 flex-1 items-center justify-center px-1">
            {/* Name des Mandanten, NICHT fest verdrahtet — sonst trägt jede Praxis die Wortmarke einer anderen. */}
            <Link to="/" className="flex min-h-11 min-w-0 items-center px-2" aria-label="Zur Startseite">
              <Logo className="h-7" textClass="text-lg" farbe="text-white" />
            </Link>
          </div>
          <div className="flex min-w-0 flex-1 items-center justify-end gap-0.5">
            <HeaderInbox count={pendingCount} />
            <HeaderBell />
            <div className="flex shrink-0 items-center pl-1 pr-2"><UserMenu /></div>
          </div>
        </div>

        {/* Main — Scroll-Container */}
        <UpdateHinweis />
        <main ref={mainRef} className="relative flex-1 overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="mx-auto max-w-[1920px] px-4 sm:px-6 lg:px-8">{children}</div>
          </div>
          <footer className="border-t border-gray-200">
            <div className="mx-auto flex max-w-[1920px] items-center justify-between gap-3 px-4 py-2 text-[10.5px] text-ps-dark-400 sm:px-6 sm:py-3 sm:text-[11px] lg:px-8">
              <span className="truncate">{branding.name}{branding.fusszeile ? <span className="hidden sm:inline"> · {branding.fusszeile}</span> : null}</span>
              <span className="inline-flex shrink-0 items-center gap-1.5">
                <LockClosedIcon className="hidden h-3 w-3 sm:inline" />
                <span className="hidden sm:inline">Daten bleiben lokal — keine Cloud</span>
                <span className="sm:hidden">🔒 Lokal</span>
              </span>
            </div>
          </footer>
          <ScrollToTopButton targetRef={mainRef} />
        </main>
      </div>
    </div>
  );
}
